/*
 * sp-marquee.js — motor de marquee del kit Shopify maker (CONTRACTS §K.3-K.4, D-42; PARITY PAR-39, 40, 55, 58-63, 120).
 *
 * window.spMarquee = { init(root, opts) → instancia, destroy(root), get(root) } — una instancia por raíz
 * [data-sp-marquee], guardada en un WeakMap (idempotente: init dos veces devuelve la misma). Auto-init de todas las raíces
 * al cargar y soporte del editor de temas: shopify:section:load/unload (init/destroy de las raíces de la sección) y
 * shopify:block:select/deselect (pausa y lleva la card ORIGINAL seleccionada al viewport; nunca el clon).
 *
 * Markup que espera (secciones sp-reviews y sp-videos): raíz [data-sp-marquee][data-speed][data-static-if-fits] >
 * .sp-marquee (viewport) > .sp-marquee__track (ul) > cards (li). Las cards se renderizan UNA vez; el motor agrega las
 * copias del grupo (data-sp-clone="<n.º de copia>", aria-hidden, sin data-shopify-editor-block ni ids, foco con
 * tabindex -1; inert en el editor). Copias = ceil((ancho del viewport + gap) / período), mínimo 1: con pocas cards (una
 * tienda nueva con 1-3 reseñas reales) una sola copia dejaba un hueco en blanco que crecía en cada vuelta. El período
 * del loop sigue siendo offsetLeft(primer clon) − offsetLeft(primera card original).
 *
 * Reglas probadas en la tienda de referencia (custom-sections §1.5.3, §6):
 *  - pointermove SIEMPRE pasivo; setPointerCapture recién al superar el umbral (8 px): antes es un click y llega a la card.
 *  - click en captura anulado si hubo arrastre; dragstart con preventDefault y pointerdown de mouse con preventDefault
 *    (con draggable="false" en las imágenes: las tres defensas contra el drag nativo).
 *  - Inercia medida en una ventana de 120 ms, tope 7000 px/s, decaimiento exp(-1,5 t), corte bajo 15 px/s; dedo quieto
 *    más de 90 ms antes de soltar = sin envión. dt con tope de 0,05 s. Wrap ((x % w) + w) % w.
 *  - Pausa por hover (mouse), foco dentro del carrusel (focusin/focusout, §K.3) y pauseWhen() (popup abierto: clase
 *    is-open en la raíz). El foco de teclado (:focus-visible) además trae la card a la vista.
 *  - IntersectionObserver: fuera del viewport se CANCELA el rAF y se relanza al volver (mejora sobre seguir pidiendo
 *    frames y salir temprano).
 *  - prefers-reduced-motion: sin motor y sin clon (is-manual: scroll horizontal manual por CSS).
 *  - staticIfFits: si un grupo entra en el ancho, queda quieto y centrado (is-static: sin clon y sin arrastre); vale
 *    también con reduced-motion, porque quieto no hay movimiento.
 */
(function () {
  'use strict';
  if (window.spMarquee && window.spMarquee.__kit) return; // el asset se carga desde cada sección: una sola definición

  var DEFAULTS = {
    speed: 45, // px/s de la marcha automática
    staticIfFits: false,
    clone: true,
    dragThreshold: 8, // px: hasta acá es un click
    sampleWindow: 120, // ms: ventana para medir la velocidad del envión
    maxFling: 7000, // px/s
    decay: 1.5, // extraV *= exp(-dt * decay)
    minV: 15, // px/s: debajo de esto el envión se corta
    quietMs: 90, // ms: dedo quieto antes de soltar = sin envión
    clickGuardMs: 600, // ms: un click que llega hasta acá después de un arrastre se anula
    pauseWhen: null
  };
  var CLASSES = ['is-running', 'is-static', 'is-manual'];
  var MAX_COPIES = 40; // tope de seguridad: 1 card de video (264 px) en un monitor 8K pide 30 copias
  var FOCUSABLE = 'a[href], button, input, select, textarea, video, audio, [tabindex], [contenteditable]';
  var instances = new WeakMap();
  var reduceMq = window.matchMedia ? window.matchMedia('(prefers-reduced-motion: reduce)') : null;

  function designMode() {
    return !!(window.Shopify && window.Shopify.designMode);
  }
  function toNumber(v, d) {
    var n = parseFloat(v);
    return isFinite(n) && n > 0 ? n : d;
  }
  function toBool(v, d) {
    if (v === true || v === false) return v;
    if (v === undefined || v === null) return d;
    var s = String(v).trim().toLowerCase();
    if (s === '' || s === 'true' || s === '1') return true;
    if (s === 'false' || s === '0') return false;
    return d;
  }
  function readOptions(root, opts) {
    var o = {};
    var k;
    for (k in DEFAULTS) o[k] = DEFAULTS[k];
    var ds = root.dataset || {};
    if (ds.speed !== undefined) o.speed = toNumber(ds.speed, o.speed);
    if (ds.staticIfFits !== undefined) o.staticIfFits = toBool(ds.staticIfFits, o.staticIfFits);
    if (opts) for (k in opts) if (opts[k] !== undefined) o[k] = opts[k];
    o.speed = toNumber(o.speed, DEFAULTS.speed);
    return o;
  }

  function Marquee(root, opts) {
    this.root = root;
    this.o = readOptions(root, opts);
    this.viewport = this.o.viewport || root.querySelector('.sp-marquee');
    this.track = this.o.track || (this.viewport ? this.viewport.querySelector('.sp-marquee__track') : null);
    this.mode = 'idle';
    this.offset = 0;
    this.extraV = 0;
    this.groupW = 0;
    this.hovering = false;
    this.focused = false;
    this.pressing = false;
    this.dragging = false;
    this.dragged = false;
    this.selected = null;
    this.inView = true;
    this.rafId = 0;
    this.prevT = 0;
    this.renderedPx = null;
    this.samples = [];
    this.pointerId = null;
    this.startX = 0;
    this.startOffset = 0;
    this.releaseT = 0;
    this.clones = []; // todas las cards clonadas, en orden
    this.copies = []; // las mismas, agrupadas por copia del grupo (copies[0] = la primera después de los originales)
    this.bound = [];
    this.io = null;
    this.ro = null;
    this.layoutRaf = 0;
    this._tick = this.tick.bind(this);
  }

  Marquee.prototype.on = function (target, type, fn, options) {
    if (!target) return;
    if (target.addEventListener) target.addEventListener(type, fn, options);
    else if (target.addListener) target.addListener(fn); // MediaQueryList viejos
    this.bound.push([target, type, fn, options]);
  };

  Marquee.prototype.start = function () {
    var self = this;
    var vp = this.viewport;
    this.on(vp, 'pointerdown', function (e) { self.onDown(e); });
    // pasivo: nunca hace preventDefault, así no frena el gesto (PAR-39)
    this.on(vp, 'pointermove', function (e) { self.onMove(e); }, { passive: true });
    this.on(vp, 'pointerup', function (e) { self.onUp(e); });
    this.on(vp, 'pointercancel', function (e) { self.onUp(e); });
    // sólo si se pierde NUESTRA captura (la del viewport): en táctil el navegador captura implícitamente el puntero en la
    // card del pointerdown y, al pasar la captura al viewport tras el umbral, ese lostpointercapture de la card sube hasta
    // acá; si cortara el arrastre, el swipe no movería el carrusel (ronda 1)
    this.on(vp, 'lostpointercapture', function (e) { if (e.target === vp) self.onUp(e); });
    // un arrastre no abre el popup: click en CAPTURA, antes que el de la sección
    this.on(vp, 'click', function (e) { self.onClickCapture(e); }, true);
    // las fotos de las cards no inician el drag nativo de imagen
    this.on(vp, 'dragstart', function (e) { e.preventDefault(); });
    this.on(vp, 'pointerenter', function (e) { if (e.pointerType === 'mouse') self.hovering = true; });
    this.on(vp, 'pointerleave', function (e) { if (e.pointerType === 'mouse') self.hovering = false; });
    this.on(vp, 'focusin', function (e) { self.onFocusIn(e); });
    this.on(vp, 'focusout', function (e) { self.onFocusOut(e); });
    this.on(vp, 'scroll', function () { self.onScroll(); }, { passive: true });
    if (reduceMq) this.on(reduceMq, 'change', function () { self.layout(); });

    if ('IntersectionObserver' in window) {
      this.io = new IntersectionObserver(function (entries) {
        for (var i = 0; i < entries.length; i++) self.inView = entries[i].isIntersecting;
        if (self.inView) self.kick();
        else if (!self.dragging) self.stop();
      });
      this.io.observe(vp);
    }
    if ('ResizeObserver' in window) {
      this.ro = new ResizeObserver(function () { self.scheduleLayout(); });
      this.ro.observe(vp);
    } else {
      this.on(window, 'resize', function () { self.scheduleLayout(); });
    }
    this.on(window, 'load', function () { self.scheduleLayout(); });
    this.layout();
  };

  Marquee.prototype.originals = function () {
    var out = [];
    var kids = this.track.children;
    for (var i = 0; i < kids.length; i++) if (!kids[i].hasAttribute('data-sp-clone')) out.push(kids[i]);
    return out;
  };

  /** ¿Un grupo de cards entra completo en el ancho del viewport? */
  Marquee.prototype.fits = function (orig) {
    if (!orig.length) return true;
    var first = orig[0].getBoundingClientRect();
    var last = orig[orig.length - 1].getBoundingClientRect();
    var cs = window.getComputedStyle(this.track);
    var pad = (parseFloat(cs.paddingLeft) || 0) + (parseFloat(cs.paddingRight) || 0);
    return last.right - first.left + pad <= this.viewport.clientWidth + 1;
  };

  Marquee.prototype.scheduleLayout = function () {
    var self = this;
    if (this.layoutRaf) return;
    this.layoutRaf = requestAnimationFrame(function () {
      self.layoutRaf = 0;
      self.layout();
    });
  };

  /**
   * Decide el modo y mide el período del loop: static si un grupo entra (también con reduced-motion: quieto y centrado
   * no tiene movimiento), manual con reduced-motion o sin clon (scroll horizontal) y running en el resto. En running
   * ajusta las copias del grupo al ancho actual del viewport (syncClones) antes de medir.
   */
  Marquee.prototype.layout = function () {
    var orig = this.originals();
    var mode;
    if (!orig.length) mode = 'manual';
    else if (this.o.staticIfFits && this.fits(orig)) mode = 'static';
    else if ((reduceMq && reduceMq.matches) || this.o.clone === false) mode = 'manual';
    else mode = 'running';
    this.setMode(mode);
    if (mode === 'running') {
      this.syncClones();
      this.measure();
      this.render();
      this.kick();
    }
  };

  Marquee.prototype.setMode = function (mode) {
    if (this.mode === mode) return;
    var root = this.root;
    for (var i = 0; i < CLASSES.length; i++) root.classList.remove(CLASSES[i]);
    root.classList.add('is-' + mode);
    if (mode === 'running') {
      this.track.style.willChange = 'transform'; // las copias las pone syncClones() en cada layout
    } else {
      this.stop();
      this.removeClones();
      this.offset = 0;
      this.extraV = 0;
      this.groupW = 0;
      this.renderedPx = null;
      this.track.style.transform = '';
      this.track.style.willChange = '';
      this.viewport.classList.remove('is-dragging');
      this.pressing = false;
      this.dragging = false;
    }
    this.mode = mode;
  };

  /**
   * Copias del grupo para el loop: tantas como hagan falta para que el track cubra un período más el ancho del viewport
   * en cualquier offset (copias · período ≥ ancho del viewport + gap), mínimo 1 y con tope de seguridad. Corre en cada
   * layout (resize, load): agrega o saca copias al final del track.
   */
  Marquee.prototype.syncClones = function () {
    var orig = this.originals();
    if (!orig.length) { this.removeClones(); return; }
    if (this.copies.length && this.copies[0].length !== orig.length) this.removeClones(); // cambió el grupo
    if (!this.copies.length) this.addCopy(orig);
    var first = orig[0];
    var last = orig[orig.length - 1];
    var period = this.copies[0][0].offsetLeft - first.offsetLeft;
    var gap = Math.max(0, period - (last.offsetLeft + last.offsetWidth - first.offsetLeft));
    var need = period > 0 ? Math.ceil((this.viewport.clientWidth + gap) / period) : 1;
    need = Math.min(MAX_COPIES, Math.max(1, need));
    while (this.copies.length < need) this.addCopy(orig);
    while (this.copies.length > need) this.removeCopy();
  };

  /** Una copia más del grupo al final del track (n.º de copia en data-sp-clone). */
  Marquee.prototype.addCopy = function (orig) {
    var n = this.copies.length + 1;
    var inert = designMode();
    var frag = document.createDocumentFragment();
    var set = [];
    for (var i = 0; i < orig.length; i++) {
      var c = orig[i].cloneNode(true);
      c.setAttribute('data-sp-clone', String(n));
      c.setAttribute('aria-hidden', 'true');
      var all = [c].concat(Array.prototype.slice.call(c.querySelectorAll('*')));
      for (var j = 0; j < all.length; j++) {
        all[j].removeAttribute('data-shopify-editor-block');
        all[j].removeAttribute('id');
      }
      var focusables = c.querySelectorAll(FOCUSABLE);
      for (var f = 0; f < focusables.length; f++) focusables[f].setAttribute('tabindex', '-1');
      if (inert) {
        c.setAttribute('inert', '');
        c.inert = true;
      }
      set.push(c);
      this.clones.push(c);
      frag.appendChild(c);
    }
    this.copies.push(set);
    this.track.appendChild(frag);
  };

  /** Saca la última copia del grupo. */
  Marquee.prototype.removeCopy = function () {
    var set = this.copies.pop();
    if (!set) return;
    for (var i = 0; i < set.length; i++) if (set[i].parentNode) set[i].parentNode.removeChild(set[i]);
    this.clones.length = Math.max(0, this.clones.length - set.length);
  };

  /** Saca todas las copias (modo static/manual, destroy). */
  Marquee.prototype.removeClones = function () {
    for (var i = 0; i < this.clones.length; i++) {
      var c = this.clones[i];
      if (c.parentNode) c.parentNode.removeChild(c);
    }
    this.clones.length = 0;
    this.copies.length = 0;
  };

  Marquee.prototype.measure = function () {
    var orig = this.originals();
    if (!orig.length || !this.clones.length) { this.groupW = 0; return; }
    var w = this.clones[0].offsetLeft - orig[0].offsetLeft;
    this.groupW = w > 0 ? w : 0;
  };

  Marquee.prototype.render = function () {
    var w = this.groupW;
    if (w > 0) this.offset = ((this.offset % w) + w) % w;
    var px = Math.round(this.offset * 100) / 100;
    if (px !== this.renderedPx) {
      this.renderedPx = px;
      this.track.style.transform = 'translate3d(' + (-px) + 'px, 0, 0)';
    }
  };

  Marquee.prototype.isPaused = function () {
    if (this.hovering || this.focused || this.selected) return true;
    if (typeof this.o.pauseWhen === 'function') return !!this.o.pauseWhen(this.root);
    return this.root.classList.contains('is-open');
  };

  Marquee.prototype.kick = function () {
    if (this.mode !== 'running' || this.rafId) return;
    if (!this.inView && !this.dragging) return;
    this.prevT = 0;
    this.rafId = requestAnimationFrame(this._tick);
  };

  Marquee.prototype.stop = function () {
    if (this.rafId) cancelAnimationFrame(this.rafId);
    this.rafId = 0;
  };

  Marquee.prototype.tick = function (t) {
    this.rafId = 0;
    if (this.mode !== 'running') return;
    var dt = this.prevT ? Math.min(0.05, Math.max(0, (t - this.prevT) / 1000)) : 0;
    this.prevT = t;
    if (!this.dragging && this.groupW > 0) {
      var base = this.isPaused() ? 0 : this.o.speed;
      if (base || this.extraV) {
        this.offset += (base + this.extraV) * dt;
        if (this.extraV) {
          this.extraV *= Math.exp(-dt * this.o.decay);
          if (Math.abs(this.extraV) < this.o.minV) this.extraV = 0;
        }
      }
    }
    this.render();
    if (this.inView || this.dragging) this.rafId = requestAnimationFrame(this._tick);
  };

  Marquee.prototype.onDown = function (e) {
    if (this.mode !== 'running') return; // quieto o manual: no hay nada que arrastrar
    if (e.button !== undefined && e.button !== 0) return;
    if (e.pointerType === 'mouse') e.preventDefault(); // mata el drag nativo de la foto
    this.pressing = true;
    this.dragging = false;
    this.dragged = false;
    this.extraV = 0; // apoyar el dedo frena el envión, como un scroller nativo
    this.pointerId = e.pointerId;
    this.startX = e.clientX;
    this.startOffset = this.offset;
    this.samples.length = 0;
    this.kick();
  };

  Marquee.prototype.onMove = function (e) {
    if (!this.pressing || e.pointerId !== this.pointerId) return;
    var now = performance.now();
    if (!this.dragging) {
      // click vs drag: el puntero no se captura hasta superar el umbral, así el click limpio llega a la card
      if (Math.abs(e.clientX - this.startX) > this.o.dragThreshold) {
        this.dragging = true;
        this.dragged = true;
        this.startX = e.clientX;
        this.startOffset = this.offset;
        this.samples.length = 0;
        this.samples.push({ t: now, x: e.clientX });
        this.viewport.classList.add('is-dragging');
        try { this.viewport.setPointerCapture(e.pointerId); } catch (err) { /* puntero ya liberado */ }
        this.kick();
      }
      return;
    }
    this.samples.push({ t: now, x: e.clientX });
    while (this.samples.length > 2 && now - this.samples[0].t > this.o.sampleWindow) this.samples.shift();
    this.offset = this.startOffset - (e.clientX - this.startX);
  };

  Marquee.prototype.onUp = function (e) {
    if (e && this.pointerId !== null && e.pointerId !== this.pointerId) return;
    var wasDragging = this.dragging;
    this.pressing = false;
    this.pointerId = null;
    if (!wasDragging) return;
    this.dragging = false;
    this.viewport.classList.remove('is-dragging');
    this.releaseT = performance.now();
    var vx = 0;
    var s = this.samples;
    if (s.length >= 2) {
      var a = s[0];
      var z = s[s.length - 1];
      var lapse = z.t - a.t;
      // si el dedo estuvo quieto antes de soltar, no hay envión
      if (lapse > 8 && this.releaseT - z.t < this.o.quietMs) vx = ((z.x - a.x) / lapse) * 1000;
    }
    this.extraV = Math.max(-this.o.maxFling, Math.min(this.o.maxFling, -vx));
    s.length = 0;
    this.kick();
  };

  Marquee.prototype.onClickCapture = function (e) {
    if (!this.dragged) return;
    this.dragged = false;
    if (performance.now() - this.releaseT < this.o.clickGuardMs) {
      e.preventDefault();
      e.stopPropagation();
    }
  };

  Marquee.prototype.cardOf = function (el) {
    while (el && el.parentElement !== this.track) el = el.parentElement;
    return el || null;
  };

  Marquee.prototype.onFocusIn = function (e) {
    this.focused = true; // §K.3: el foco dentro del carrusel lo pausa hasta que sale (focusout)
    var visible = true;
    try { visible = e.target.matches(':focus-visible'); } catch (err) { visible = true; }
    // sólo el foco de teclado trae la card a la vista: un toque o un click no mueven el carrusel debajo del puntero
    if (visible && this.mode === 'running') {
      var card = this.cardOf(e.target);
      if (card) this.ensureVisible(card, false);
    }
  };

  Marquee.prototype.onFocusOut = function (e) {
    if (!e.relatedTarget || !this.viewport.contains(e.relatedTarget)) this.focused = false;
  };

  // el foco puede desplazar el viewport (overflow hidden igual scrollea por programa): se pasa ese desplazamiento al offset
  Marquee.prototype.onScroll = function () {
    if (this.mode !== 'running') return;
    var sl = this.viewport.scrollLeft;
    if (sl) {
      this.viewport.scrollLeft = 0;
      this.offset += sl;
      this.render();
    }
  };

  /** Lleva la card al viewport (centrada si hace falta); en modo running mueve el offset, si no scrollea nativo. */
  Marquee.prototype.ensureVisible = function (card, force) {
    var v = this.viewport.getBoundingClientRect();
    var r = card.getBoundingClientRect();
    if (!force && r.left >= v.left && r.right <= v.right) return;
    if (this.mode === 'running') {
      var t = this.track.getBoundingClientRect();
      var pos = r.left - t.left;
      if (card.hasAttribute('data-sp-clone')) pos -= this.groupW * (parseInt(card.getAttribute('data-sp-clone'), 10) || 1);
      this.offset = Math.max(0, pos - Math.max(0, (v.width - r.width) / 2));
      this.extraV = 0;
      this.render();
    } else if (card.scrollIntoView) {
      card.scrollIntoView({ block: 'nearest', inline: 'center' });
    }
  };

  /** Editor de temas: block:select pausa y muestra la card original; nunca selecciona el clon. */
  Marquee.prototype.select = function (el) {
    var card = this.cardOf(el);
    if (!card || card.hasAttribute('data-sp-clone')) return false;
    this.selected = card;
    this.ensureVisible(card, true);
    return true;
  };

  Marquee.prototype.deselect = function () {
    this.selected = null;
    this.kick();
  };

  Marquee.prototype.destroy = function () {
    this.stop();
    if (this.layoutRaf) cancelAnimationFrame(this.layoutRaf);
    this.layoutRaf = 0;
    if (this.io) this.io.disconnect();
    if (this.ro) this.ro.disconnect();
    this.io = null;
    this.ro = null;
    for (var i = 0; i < this.bound.length; i++) {
      var b = this.bound[i];
      if (b[0].removeEventListener) b[0].removeEventListener(b[1], b[2], b[3]);
      else if (b[0].removeListener) b[0].removeListener(b[2]);
    }
    this.bound.length = 0;
    this.removeClones();
    this.track.style.transform = '';
    this.track.style.willChange = '';
    this.viewport.classList.remove('is-dragging');
    for (var c = 0; c < CLASSES.length; c++) this.root.classList.remove(CLASSES[c]);
    this.mode = 'destroyed';
    this.selected = null;
    instances.delete(this.root);
  };

  function rootsIn(node) {
    var out = [];
    if (!node || node.nodeType !== 1) return out;
    if (node.matches('[data-sp-marquee]')) out.push(node);
    var found = node.querySelectorAll('[data-sp-marquee]');
    for (var i = 0; i < found.length; i++) out.push(found[i]);
    return out;
  }

  function instanceFor(node) {
    var root = node && node.nodeType === 1 && node.closest ? node.closest('[data-sp-marquee]') : null;
    return root ? instances.get(root) : undefined;
  }

  var api = {
    __kit: 1,
    version: '1.0.0',
    defaults: DEFAULTS,
    init: function (root, opts) {
      if (!root || root.nodeType !== 1) return null;
      var inst = instances.get(root);
      if (inst) return inst;
      inst = new Marquee(root, opts);
      if (!inst.viewport || !inst.track) return null;
      instances.set(root, inst);
      inst.start();
      return inst;
    },
    destroy: function (root) {
      var inst = root ? instances.get(root) : undefined;
      if (!inst) return false;
      inst.destroy();
      return true;
    },
    get: function (root) {
      return root ? instances.get(root) : undefined;
    },
    initAll: function (scope) {
      var roots = rootsIn(scope && scope.nodeType === 1 ? scope : document.documentElement);
      for (var i = 0; i < roots.length; i++) api.init(roots[i]);
    },
    destroyAll: function (scope) {
      var roots = rootsIn(scope && scope.nodeType === 1 ? scope : document.documentElement);
      for (var i = 0; i < roots.length; i++) api.destroy(roots[i]);
    }
  };
  window.spMarquee = api;

  // Editor de temas (D-42): re-render = unload del nodo viejo + load del nuevo
  document.addEventListener('shopify:section:unload', function (e) { api.destroyAll(e.target); });
  document.addEventListener('shopify:section:load', function (e) { api.initAll(e.target); });
  document.addEventListener('shopify:block:select', function (e) {
    var inst = instanceFor(e.target);
    if (inst) inst.select(e.target);
  });
  document.addEventListener('shopify:block:deselect', function (e) {
    var inst = instanceFor(e.target);
    if (inst) inst.deselect();
  });

  if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', function () { api.initAll(); });
  else api.initAll();
})();
